import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { assignAccountTenant, unassignAccountTenant } from '../../api/endpoints';
import { ApiError } from '../../api/http';
import { queryKeys } from '../../api/queryKeys';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';

interface Assignment {
  accountGid: string;
  tenantGid: string;
}

/**
 * Placing an account with a tenant and taking it back, mirroring how a person is assigned one. Both
 * are idempotent at the API, so a retry after a timeout is not an error.
 */
export function useAccountAssignment() {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
    await queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
  };

  const assign = useMutation({
    mutationFn: ({ accountGid, tenantGid }: Assignment) => assignAccountTenant(accountGid, tenantGid),
    onSuccess: async () => {
      await invalidate();
      toastSuccess(t('admin.accounts.assigned'));
    },
    onError: toastError,
  });

  const unassign = useMutation({
    mutationFn: ({ accountGid, tenantGid }: Assignment) => unassignAccountTenant(accountGid, tenantGid),
    onSuccess: async () => {
      await invalidate();
      toastSuccess(t('admin.accounts.returnedToPool'));
    },
    onError: (error) => {
      // 409 means something already went through the account, which disabling solves and unassigning
      // cannot — the same shape of refusal as deleting one.
      if (error instanceof ApiError && error.status === 409) {
        toastError(
          new ApiError(409, {
            detail: `${error.problem?.detail ?? t('admin.tenantAccounts.usedFallback')} ${t('admin.accounts.usedUnassignSuffix')}`,
          }),
        );
      } else {
        toastError(error);
      }
    },
  });

  return { assign, unassign };
}
